// Работен лист „Прехвърляне" (`/inventory/transfer/new` и
// `/inventory/transfer/:transferId/edit`, вътре в Inventory layout-а) — по
// модела на листа „Доставка": хедър със стрелка „← Прехвърляне", карта с
// полетата, търсачка + „Целият списък", карта „Продукти" (решетка на десктоп,
// карти под 1024px) и долна лента с обобщението и действията.
//
// Функционалността е непроменена: наличността/партидите/FIFO цената идват от
// ИЗТОЧНИКА КЪМ ИЗБРАНАТА ДАТА, не може да се прехвърли над наличното, а
// себестойността е ВИНАГИ нето (бруто по регистрацията по ДДС).
//
// Части в ./components: Header, Search, ProductsCard → Table/LineCards с
// `useTransferRowsActions`, Footer, `useTransferLoad`, `useTransferLeave`,
// `transferDraft`. Новото прехвърляне пази чернова (браузър + сървър, със
// заключване — общите `components/drafts/`): „Запази временно", продължаване
// от началния екран, въпрос при директно отваряне с незавършено.
// Клавиатурата е общата за модула (gridNavigation / useWorksheetSearch /
// useWorksheetKeyboard); Ctrl/Cmd+Enter записва. След запис листът се връща в
// началния екран на операцията.
import { useEffect, useMemo, useRef, useState } from "react";
import { App } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import {
  useCreateTransfer,
  useInventorySettings,
  useProducts,
  useStockBalances,
  useUpdateTransfer,
  useWarehouses,
} from "../../../hooks/useInventory";
import type { CreateTransferPayload, Transfer } from "../../../api/inventory";
import type { TransferDocument } from "../../../api/inventoryTransfers";
import { withCurrentTime } from "../components/dateTime";
import { clampDecimals } from "../components/qty";
import { pickableProducts } from "../components/product/deletedProducts";
import { formatQtyInput, parseDecimal } from "../components/numbers";
import { useGridNavigation } from "../components/gridNavigation";
import { useWorksheetKeyboard } from "../components/useWorksheetKeyboard";
import { useWorksheetSearch } from "../components/useWorksheetSearch";
import { InventoryTheme } from "../components/InventoryTheme";
import { CatalogPickerModal, type CatalogEntryValues } from "../components/search/CatalogPickerModal";
import type { DraftRouterState } from "../components/drafts/draftModel";
import { DraftPresence } from "../components/drafts/DraftWorksheetParts";
import { DraftResumePrompt } from "../components/drafts/DraftResumePrompt";
import { DRAFT_TEXTS } from "../components/drafts/draftTexts";
import { TransferWorksheetHeader } from "./components/TransferWorksheetHeader";
import { TransferWorksheetSearch, type TransferSearchRow } from "./components/TransferWorksheetSearch";
import { TransferProductsCard } from "./components/TransferProductsCard";
import { TransferWorksheetFooter } from "./components/TransferWorksheetFooter";
import { useTransferRowsActions } from "./components/useTransferRowsActions";
import { useTransferLoad } from "./components/useTransferLoad";
import { useTransferLeave } from "./components/useTransferLeave";
import { useTransferDraft } from "./components/useTransferDraft";
import { transferDraftDate } from "./components/transferDraft";
import {
  TRANSFER_COL_COUNT,
  emptyTransferLine,
  sourceCatalogItemsOf,
  type TransferLineDraft,
} from "./components/transferWorksheetShared";

const HOME_PATH = "/inventory/transfer";

// Редакцията идва с документа; новото — с избора за черновата от началния екран
type TransferRouterState = (DraftRouterState & { transfer?: TransferDocument }) | null;

type Props = {
  mode: "new" | "edit";
  // складът от конфигурацията — начален източник при ново прехвърляне
  warehouseId?: number | null;
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

export const TransferWorksheetPage = ({ mode, warehouseId = null, onRegisterLeaveGuard }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { transferId } = useParams();
  const location = useLocation();
  const { isDesktop, isNarrow } = useViewport();
  const routerState = (location.state as TransferRouterState) ?? null;

  const { data: warehouses = [] } = useWarehouses();
  const { data: products = [] } = useProducts();
  const { data: settings } = useInventorySettings();
  // Себестойността (FIFO) е ВИНАГИ нето; бруто по регистрацията по ДДС.
  const vatRegistered = settings?.vatRegistered ?? true;
  const createMutation = useCreateTransfer();
  const updateMutation = useUpdateTransfer();

  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [fromWarehouseId, setFromWarehouseId] = useState<number | null>(warehouseId);
  const [toWarehouseId, setToWarehouseId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<TransferLineDraft[]>([]);
  const [catalogOpen, setCatalogOpen] = useState(false);

  // ─── Търсачката и решетката (общата клавиатура на модула) ────────────────
  // Колони: 0 Количество · 1 Партида. Ключът на реда е id-то на продукта;
  // вече добавен продукт от търсачката фокусира СЪЩЕСТВУВАЩИЯ си ред.
  const searchBox = useWorksheetSearch({
    getResultCount: () => searchRows.length,
    onPickIndex: (index) => addProduct(searchRows[index].product.id),
    focusFirst: () => focusFirst(0),
  });
  const rowProductIdsRef = useRef<number[]>([]);
  const { focusCell, focusFirst, focusSideways, onGridKey, isInGrid } = useGridNavigation({
    prefix: "trx",
    colCount: TRANSFER_COL_COUNT,
    getRowKeys: () => rowProductIdsRef.current,
    onEscape: searchBox.focusSearch,
    onLeaveTop: searchBox.focusSearch,
    enterOpensCols: [1], // „Партида" отваря падащото си меню
  });

  // Складът от конфигурацията пристига асинхронно — попълва се, щом дойде
  useEffect(() => {
    if (mode === "new" && fromWarehouseId == null && warehouseId != null)
      setFromWarehouseId(warehouseId);
  }, [mode, warehouseId, fromWarehouseId]);

  // ─── Зареждане при редакция (router state или по id) ─────────────────────
  const { editedTransfer, savedQuantities, savedDate } = useTransferLoad({
    mode,
    transferId,
    transferFromState: routerState?.transfer,
    products,
    actions: {
      setDate,
      setFromWarehouseId,
      setToWarehouseId,
      setNote,
      setLines,
      onLoadError: () => {
        message.error(t("inventoryPage.errLoad"));
        navigate(HOME_PATH);
      },
    },
  });

  // ─── „Незапазени промени" + черновата на новото (браузър + сървър) ──────
  // Снимката се чете лениво — сумите идват от hook-а по-долу.
  const warehouseNameOf = (id: number | null) =>
    warehouses.find((warehouse) => warehouse.id === id)?.name ?? null;
  const draft = useTransferDraft({
    mode,
    homePath: HOME_PATH,
    routerState,
    products,
    warehouseNameOf,
    sheet: () => ({ date, fromWarehouseId, toWarehouseId, note, lines, totalGross }),
    apply: (payload) => {
      setDate(transferDraftDate(payload));
      setFromWarehouseId(payload.fromWarehouseId ?? warehouseId);
      setToWarehouseId(payload.toWarehouseId ?? null);
      setNote(payload.note ?? "");
      setLines(payload.lines);
    },
    onLoadError: () => {
      message.error(t("inventoryPage.errLoad"));
      navigate(HOME_PATH);
    },
  });
  const { dirtyRef, touch, resume } = draft;
  const requestLeave = useTransferLeave({
    mode,
    lines,
    products,
    dirtyRef,
    flushDraft: draft.sync.flush,
    onRegisterLeaveGuard,
  });
  const leaveToHome = () => requestLeave(() => navigate(HOME_PATH));

  // ─── Редовете и сметките ─────────────────────────────────────────────────
  // Наличностите към датата: на ПРИЕМНИКА — за колоната „Става"; на
  // ИЗТОЧНИКА — за търсачката и „Целия списък"
  const asOfDay = date ? date.format("YYYY-MM-DD") : null;
  const { data: targetBalances = [] } = useStockBalances(toWarehouseId, asOfDay);
  const { data: sourceBalances = [] } = useStockBalances(fromWarehouseId, asOfDay);
  const { rows, totalNet, totalGross, filledRows, overStockNames, buildLines } =
    useTransferRowsActions({
      lines,
      products,
      fromWarehouseId,
      date,
      targetBalances,
      vatRegistered,
      // Редакция: наличността към датата е вече намалена със записания документ
      savedQuantities,
      savedDate,
    });
  // Редът на редовете за ↑/↓ в решетката
  rowProductIdsRef.current = rows.map((row) => row.productId);

  const addedProductIds = useMemo(() => new Set(lines.map((line) => line.productId)), [lines]);
  // Фокусът отива в „Количество" на реда (редът се появява след re-render)
  const focusQuantity = (productId: number) => focusCell(productId, 0);
  const addProduct = (productId: number) => {
    touch();
    if (!addedProductIds.has(productId))
      setLines((current) => [...current, emptyTransferLine(productId)]);
    searchBox.setSearch("");
    focusQuantity(productId);
  };
  // „Целият списък": новите редове в реда на каталога, фокус в първия избран.
  // Въведеното количество (по мерната единица) отива в реда — и във вече
  // добавения (без дубликат; попълненият модалът е презаписал след въпрос).
  const addProducts = (productIds: number[], values?: CatalogEntryValues) => {
    setCatalogOpen(false);
    const fresh = productIds.filter((productId) => !addedProductIds.has(productId));
    const quantityOf = (productId: number) => {
      const typed = values?.get(productId)?.qty ?? "";
      const decimals = clampDecimals(products.find((product) => product.id === productId)?.decimalPlaces);
      return typed === "" ? null : formatQtyInput(parseDecimal(typed), decimals);
    };
    if (fresh.length || values?.size) {
      touch();
      setLines((current) => [
        ...current.map((line) => {
          const quantity = quantityOf(line.productId);
          return quantity == null ? line : { ...line, quantity };
        }),
        ...fresh.map((productId) => ({ ...emptyTransferLine(productId), quantity: quantityOf(productId) ?? "" })),
      ]);
    }
    if (productIds[0] != null) focusQuantity(productIds[0]);
  };
  const patchLine = (productId: number, patch: Partial<TransferLineDraft>) => {
    touch();
    setLines((current) =>
      current.map((line) => (line.productId === productId ? { ...line, ...patch } : line)),
    );
  };
  const removeLine = (productId: number) => {
    touch();
    setLines((current) => current.filter((line) => line.productId !== productId));
  };
  // Количеството се форматира по мерната единица при напускане на клетката
  const formatQuantityOnBlur = (productId: number) => {
    const line = lines.find((item) => item.productId === productId);
    if (!line || line.quantity.trim() === "") return;
    const decimals = clampDecimals(products.find((product) => product.id === productId)?.decimalPlaces);
    patchLine(productId, { quantity: formatQtyInput(parseDecimal(line.quantity), decimals) });
  };

  // ─── Търсачка и „Целият списък" ──────────────────────────────────────────
  const { searchWords } = searchBox;
  const sourceBalanceByProduct = useMemo(
    () => new Map(sourceBalances.map((balance) => [balance.productId, balance] as const)),
    [sourceBalances],
  );
  const searchRows = useMemo<TransferSearchRow[]>(() => {
    if (searchWords.length === 0) return [];
    return pickableProducts(products)
      .filter((product) => {
        const name = product.name.toLowerCase();
        return searchWords.every((word) => name.includes(word));
      })
      .sort((a, b) => a.name.localeCompare(b.name, "bg"))
      .slice(0, 40)
      .map((product) => {
        const balance = sourceBalanceByProduct.get(product.id);
        return {
          product,
          added: addedProductIds.has(product.id),
          available: Math.max(0, balance?.quantity ?? 0),
          unitPrice: balance?.weightedAvgPrice ?? null,
          decimals: clampDecimals(product.decimalPlaces),
        };
      });
  }, [products, searchWords, sourceBalanceByProduct, addedProductIds]);
  const catalogItems = useMemo(
    () =>
      sourceCatalogItemsOf(products, sourceBalanceByProduct, addedProductIds, t, (productId) =>
        lines.find((line) => line.productId === productId)?.quantity ?? ""),
    [products, sourceBalanceByProduct, addedProductIds, t, lines],
  );

  // ─── Валидация и запис ───────────────────────────────────────────────────
  const sameWarehouse = fromWarehouseId != null && fromWarehouseId === toWarehouseId;
  const issue = !date
    ? t("inventoryPage.errDate")
    : !fromWarehouseId
      ? t("inventoryPage.errFromWarehouse")
      : !toWarehouseId
        ? t("inventoryPage.errToWarehouse")
        : sameWarehouse
          ? t("inventoryPage.errSameWarehouse")
          : filledRows.length === 0
            ? t("inventoryPage.errNoLines")
            : overStockNames.length > 0
              ? t("inventoryPage.errStock", { products: overStockNames.join(", ") })
              : null;
  const saving = createMutation.isPending || updateMutation.isPending;

  const submit = () => {
    // Ctrl+Enter може да дойде, докато записът още тече
    if (saving) return;
    if (issue) return message.error(issue);
    const payload: CreateTransferPayload = {
      fromWarehouseId: fromWarehouseId as number,
      toWarehouseId: toWarehouseId as number,
      date: (date as Dayjs).toISOString(),
      note: note.trim() || null,
      lines: buildLines(),
    };
    // Началният екран показва toast „Виж документа" (state.openDoc)
    const onSuccess = (saved: Transfer) => {
      draft.onSaved(); // „незапазено" пада, черновата на новото изчезва
      navigate(HOME_PATH, { state: { openDoc: saved } });
    };
    const onError = (error: unknown) => {
      const failure = error as { response?: { data?: { detail?: string } } };
      message.error(failure.response?.data?.detail || t("inventoryPage.errTransfer"));
    };
    if (editedTransfer) {
      updateMutation.mutate({ id: editedTransfer.id, payload }, { onSuccess, onError });
    } else {
      createMutation.mutate(payload, { onSuccess, onError });
    }
  };

  // ─── Глобални клавиши: Ctrl+Enter / Esc / ↓ / машина за писане ───────────
  useWorksheetKeyboard({
    isDesktop,
    searchInputRef: searchBox.inputRef,
    searchTextRef: searchBox.searchTextRef,
    setSearch: searchBox.setSearch,
    isInGrid,
    focusSearch: searchBox.focusSearch,
    focusFirst: () => focusFirst(0),
    onPrimary: submit,
    onLeave: leaveToHome,
    // черновата остава — изборът е с бутоните на въпроса
    prompt: { isOpen: () => resume.pending != null, close: resume.dismiss },
  });

  const title =
    mode === "edit"
      ? `${t("inventoryPage.editTransferTitle")}${transferId ? ` №${transferId}` : ""}`
      : t("inventoryPage.newTransfer");

  return (
    <InventoryTheme controlHeight={40}>
      <div className="flex h-full flex-col overflow-hidden bg-canvas text-ink">
        <TransferWorksheetHeader
          band={{
            back: { label: t("inventoryPage.transfer"), onClick: leaveToHome },
            title,
            right: mode === "new" ? <DraftPresence lockLostBy={draft.sync.lockLostBy} /> : null,
          }}
          date={date}
          onDateChange={(value) => {
            setDate(withCurrentTime(value));
            touch();
          }}
          fromWarehouseId={fromWarehouseId}
          onFromWarehouseChange={(value) => {
            setFromWarehouseId(value);
            touch();
          }}
          toWarehouseId={toWarehouseId}
          onToWarehouseChange={(value) => {
            setToWarehouseId(value);
            touch();
          }}
          warehouses={warehouses}
          sameWarehouseError={sameWarehouse}
          note={note}
          onNoteChange={(value) => {
            setNote(value);
            touch();
          }}
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto ${
            isNarrow ? "px-3 py-3" : "px-6 py-4"
          }`}
        >
          <TransferWorksheetSearch
            attachInputRef={(node) => {
              searchBox.inputRef.current = node;
            }}
            value={searchBox.search}
            onValueChange={searchBox.setSearch}
            onFocusChange={searchBox.setFocused}
            onInputKeyDown={searchBox.onInputKeyDown}
            dropdownOpen={searchBox.dropdownOpen}
            rows={searchRows}
            searchWords={searchWords}
            highlightedIndex={searchBox.highlightedIndex}
            onHighlightChange={searchBox.setHighlightedIndex}
            onPick={addProduct}
            onOpenCatalog={() => setCatalogOpen(true)}
            catalogDisabled={fromWarehouseId == null}
          />

          <TransferProductsCard
            onGridKey={onGridKey}
            table={{
              rows,
              fromWarehouseName: warehouseNameOf(fromWarehouseId),
              toWarehouseName: warehouseNameOf(toWarehouseId),
              isDesktop,
              onQuantityChange: (productId, value) => patchLine(productId, { quantity: value }),
              onQuantityBlur: formatQuantityOnBlur,
              onLotChange: (productId, lotId) => patchLine(productId, { lotId }),
              onRemove: removeLine,
              onSideExit: focusSideways,
              totalNet,
              totalGross,
            }}
          />
        </div>

        <TransferWorksheetFooter
          mode={mode}
          totalNet={totalNet}
          totalGross={totalGross}
          issue={issue}
          issueIsStock={overStockNames.length > 0}
          saving={saving}
          draft={draft.footer()}
          onCancel={leaveToHome}
          onSave={submit}
        />

        <CatalogPickerModal
          open={catalogOpen}
          title={t("inventoryPage.trxCatalogTitle", { warehouse: warehouseNameOf(fromWarehouseId) ?? "" })}
          hint={t("inventoryPage.trxCatalogHint")}
          items={catalogItems}
          priceLabel={t("inventoryPage.colUnitPrice")}
          entry={{ kind: "qty", qtyLabel: t("inventoryPage.colQty") }}
          addedSelectable
          onClose={() => setCatalogOpen(false)}
          onAdd={addProducts}
          onAfterClose={(added) => {
            if (!added) searchBox.focusSearch();
          }}
        />

        <DraftResumePrompt
          open={resume.pending != null}
          texts={DRAFT_TEXTS.transfer}
          savedAt={resume.pending?.payload.savedAt ?? null}
          savedBy={resume.pending?.payload.savedBy ?? null}
          items={draft.pendingItems.slice(0, 5)}
          total={draft.pendingItems.length}
          onContinue={resume.continueDraft}
          onDiscard={resume.discardDraft}
          onDismiss={resume.dismiss}
        />
      </div>
    </InventoryTheme>
  );
};

export default TransferWorksheetPage;
